import { lazy } from "react";

import type { NavigationRoutingObject } from "@navigationRouting/types";

import { anyAgentPermissionsGetter } from "@lib/agentConsole";
import type { I18nT } from "@lib/dashboardI18n";

const AiAgentDetailsForNRO = lazy(() =>
  import("@scenes/AgentDetails").then((module) => ({
    default: module.AgentDetails,
  }))
);

export const aiAgentDetailsNRO: NavigationRoutingObject = {
  Component: AiAgentDetailsForNRO,
  noKnockoutFallback: true,
  componentFeatureFlippers: [],
  section: "agent-console",
  // This route serves both agent and Standard detail pages, and the heading is
  // resolved before the record loads, so it cannot name either entity.
  heading: (t: I18nT<"dashboard-navigation">) => t("braze-ai-agent-details-nro-heading", "BrazeAI™ Agent Console"),
  pageNameForTracking: () => "BrazeAI™ Agent Details",
  getDeveloperNavigationAllowed: ({ featureOn, hasPermission, isImpersonating }) => {
    // A Standards flipper grants access without the custom_ai_agents
    // entitlement so Standards can be created and edited; the
    // backend scopes which agents such a workspace can actually load.
    const consoleEntitled =
      featureOn("custom_ai_agents") ||
      featureOn("agent_console_campaign_qa") ||
      featureOn("agent_console_canvas_qa");
    if (!consoleEntitled) {
      return false;
    }

    // The route serves every kind of agent, so any agent view permission lets
    // the developer in; the page then loads only an agent they may view.
    const userHasViewAgentsPermission = anyAgentPermissionsGetter("view").some((permission) =>
      hasPermission("appGroupOrAnyTeamInAppGroup", { permission })
    );

    return isImpersonating || userHasViewAgentsPermission;
  },
  // NOTE: if you want to add an 'additionalSearchTerm', please use the scaffold
  // via 'yarn scaffold' > 'nros' > 'additionalSearchTerm' and follow the prompts
  additionalSearchTerms: () => [],
  // NOTE: when adding additionalSearchableFeatures make sure one of the
  // query params of the feature matches the key name of the feature.
  // NOTE 2: if you want to add an 'additionalSearchableFeature', please use the scaffold
  // via 'yarn scaffold' > 'nros' > 'additionalSearchableFeature' and follow the prompts
  additionalSearchableFeatures: () => ({}),
  defaultRoute: "/agent_console/canvas_agents/:appGroupId",
};
